/**
 * Film registry for conscierra.au/videos (Stories #23825, #23829).
 *
 * Copy is VERBATIM from docs/specs/tsp-xcentral/working/videos-pages-2026-10-10.md §2.4
 * (BR-V-VID-004 / BR-V-VID-008). Change the spec first, never just this file.
 *
 * AU ONLY (BR-V-VID-010). The client portal is built once per region (au / sg / id / fj), but
 * Front Door routes /media/* to the film storage on conscierra.au + www only. So the route, nav
 * item and prerender are on in the AU build and off in the others, where the films could not
 * load. `videosLive()` is the single gate; routes.tsx and PublicLayout both read it.
 *
 * The sitemap (public/sitemap.xml, conscierra.au URLs) must list /videos and every film;
 * VideosGate.test.tsx fails if it and this list disagree.
 *
 * To add a film: spec §2.4 + prototype first, then an entry here, then two sitemap lines, then
 * upload conscierra-<slug>.mp4 / -720.mp4 / .jpg / .en.vtt (docs/runbooks/media-videos.md).
 *
 * To REPLACE a film's media: bump its `version` (v2, v3, …) and upload under the new names.
 * Media is cached as immutable for a year, so overwriting a blob leaves stale copies in
 * browsers. The URL slug never changes.
 */
import { resolveBuildRegion } from '../../regions/regionConfig';
import { MEDIA_BASE_URL } from '../../siteConfig';

export interface FilmFiles {
  /** 1080p MP4 (H.264/AAC), blob name. */
  hd: string;
  /** 720p MP4, blob name — offered to narrow viewports. */
  sd: string;
  /** Poster JPEG, blob name. */
  poster: string;
  /** English captions, WebVTT, blob name. */
  captions: string;
}

/** Supported frame shapes: widescreen and square. */
export type FilmAspect = '16:9' | '1:1';

export interface Film {
  /** URL slug under /videos/ (no leading slash). */
  slug: string;
  /** Page H1 + card title. */
  title: string;
  /** The one line of copy shown under the film. */
  line: string;
  /** Which site the film belongs to. */
  brand: 'xcentral' | 'conscierra';
  /** Frame shape. The player box and the index poster take this shape, so nothing letterboxes. */
  aspect: FilmAspect;
  /** Media version suffix (e.g. 'v2'). Absent on films whose media predates versioning. */
  version?: string;
  files: FilmFiles;
}

/**
 * Blob names for a Conscierra film: conscierra-<slug>[-<version>].mp4 / -720.mp4 / .jpg / .en.vtt.
 * The version, when given, comes straight after the slug.
 */
export function conscierraFiles(slug: string, version?: string): FilmFiles {
  const base = version ? `conscierra-${slug}-${version}` : `conscierra-${slug}`;
  return {
    hd: `${base}.mp4`,
    sd: `${base}-720.mp4`,
    poster: `${base}.jpg`,
    captions: `${base}.en.vtt`,
  };
}

export const films: readonly Film[] = [
  {
    slug: 'explainer',
    title: 'How Conscierra builds your platform',
    line: 'One platform, built around how you work, and yours to own.',
    brand: 'conscierra',
    aspect: '16:9',
    version: 'v5',
    files: conscierraFiles('explainer', 'v5'),
  },
  {
    slug: 'linkedin',
    title: 'Built in, not bolted on',
    line: 'Everything your business runs on, in one platform.',
    brand: 'conscierra',
    aspect: '1:1',
    files: conscierraFiles('linkedin'),
  },
  {
    slug: 'accounting',
    title: 'For accounting practices',
    line: 'A workbench for every job, client queries tracked, and any accounting package linked.',
    brand: 'conscierra',
    aspect: '1:1',
    files: conscierraFiles('accounting'),
  },
];

/** True when there is at least one Conscierra film. */
export const hasFilms = (): boolean => films.length > 0;

/** Regions whose domains have the /media Front Door route (infra/bicep/media.bicep). */
export const MEDIA_REGIONS: readonly string[] = ['au'];

/**
 * The gate for route, nav item and prerender: films exist AND this build serves a region whose
 * domains can reach /media. Evaluated per build (VITE_HOST), defaulting to AU like the rest of
 * the region config.
 */
export const videosLive = (regionCode: string = resolveBuildRegion().code): boolean =>
  hasFilms() && MEDIA_REGIONS.includes(regionCode);

export function filmBySlug(slug: string): Film | undefined {
  return films.find((f) => f.slug === slug);
}

/** Absolute-path URL for a media blob, e.g. /media/conscierra-<slug>.mp4. */
export function mediaUrl(file: string): string {
  return `${MEDIA_BASE_URL.replace(/\/+$/, '')}/${file}`;
}

/**
 * Box class + intrinsic size for a film's frame (BR-V-VID-009). The element is given the film's
 * own shape, so a square film is never letterboxed inside a 16:9 box. Square films are capped
 * narrower so they do not tower over the page on desktop.
 */
export function frameFor(aspect: FilmAspect): { box: string; width: number; height: number } {
  return aspect === '1:1'
    ? { box: 'aspect-square w-full max-w-xl mx-auto', width: 1080, height: 1080 }
    : { box: 'aspect-video w-full', width: 1920, height: 1080 };
}
